import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import { Formik, FormikProps } from 'formik';
import React, { forwardRef, useCallback } from 'react';

import {
  Box,
  EntryList,
  FieldError,
  Flex,
  List,
  Select,
  Text,
  TextField,
  Toggle,
} from '~/components/blocks';
import { numberOrHyphenRegexp } from '~/constants/regexp';
import { SOUNDS } from '~/constants/sounds';
import { toHalfNumberWithHyphen } from '~/utils/convert';

import { SOUND_INTERVAL, TEL_DELAY } from './constants';
import { SettingItem } from './SettingItem';
import { Fields } from './types';
import { validationSchema } from './validation';

type Props = {
  disabled: boolean;
  basicPlan: boolean;
  defaultValues: Fields;
  onSubmit: (values: Fields) => void;
};

const TelSettingItem = styled(List.Item)(({ theme }) =>
  css({
    backgroundColor: theme.colors.background.bg,
    borderTop: theme.borders.grey,
    borderBottom: theme.borders.grey,
  }),
);

const InputTel = styled(TextField)(({ theme }) =>
  css({
    backgroundColor: theme.colors.background.default,
  }),
);

export const SettingForm = forwardRef<FormikProps<Fields>, Props>((props, ref) => {
  const { basicPlan } = props;
  const theme = useTheme();

  const handleChangeToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, formik: FormikProps<Fields>) => {
      const targetName = event.currentTarget.name;

      let currentValue;
      if (targetName === 'tel.enable') {
        currentValue = formik.values.tel.enable;
      } else {
        currentValue = formik.values[event.currentTarget.name as keyof Fields];
      }

      const newValue = currentValue === 'enabled' ? 'disabled' : 'enabled';
      formik.setFieldValue(targetName, newValue);
    },
    [],
  );

  return (
    <Formik
      innerRef={ref}
      enableReinitialize
      initialValues={props.defaultValues}
      validationSchema={validationSchema}
      onSubmit={props.onSubmit}
    >
      {(formik) => {
        return (
          <Box>
            <Box>
              <Text size="s">通知音でお知らせします</Text>
              <Flex mt={theme.space.m} size="l">
                <EntryList>
                  <EntryList.Head>通知音</EntryList.Head>
                  <EntryList.Body>
                    <Select
                      disabled={props.disabled}
                      name="sound.name"
                      error={formik.errors.sound?.name}
                      value={formik.values.sound.name}
                      onChange={(e) => {
                        const soundName = e.currentTarget.value as keyof typeof SOUNDS;
                        SOUNDS[soundName]?.sound.play();
                        formik.handleChange(e);
                      }}
                    >
                      {Object.entries(SOUNDS).map(([key, { label }]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </EntryList.Body>
                </EntryList>
                <EntryList m={`0 0 0 ${theme.space.l}`}>
                  <EntryList.Head>スヌーズ間隔</EntryList.Head>
                  <EntryList.Body>
                    <Select
                      disabled={props.disabled}
                      name="sound.interval"
                      error={formik.errors.sound?.interval}
                      value={formik.values.sound.interval}
                      onChange={(e) => {
                        const interval = e.currentTarget.value;
                        formik.setFieldValue('sound.interval', interval === '' ? '' : +interval);
                      }}
                    >
                      <option value="">なし</option>
                      {Object.entries(SOUND_INTERVAL).map(([interval, label]) => (
                        <option key={interval} value={interval}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </EntryList.Body>
                </EntryList>
              </Flex>
            </Box>
            <Box marginTop={theme.space.l}>
              <List>
                {basicPlan && (
                  <SettingItem
                    name="受付｜電話通知"
                    content={
                      <>
                        申し込みが入ると
                        <br />
                        電話でお知らせします
                      </>
                    }
                  >
                    <Toggle
                      name="tel.enable"
                      checked={formik.values.tel.enable === 'enabled'}
                      onChange={(event) => handleChangeToggle(event, formik)}
                    />
                  </SettingItem>
                )}
                {formik.values.tel.enable === 'enabled' && (
                  <TelSettingItem>
                    <EntryList>
                      <EntryList.Head>通知先電話番号</EntryList.Head>
                      <EntryList.Body>
                        <InputTel
                          name="tel.phoneNumber"
                          type="tel"
                          disabled={props.disabled}
                          error={!!formik.errors.tel?.phoneNumber}
                          value={formik.values.tel.phoneNumber}
                          onChange={formik.handleChange}
                          onBlur={(e) => {
                            if (!numberOrHyphenRegexp.test(e.currentTarget.value)) return;
                            formik.setFieldValue(
                              'tel.phoneNumber',
                              toHalfNumberWithHyphen(e.currentTarget.value),
                            );
                          }}
                        />
                        <Flex alignItems="center" mt={theme.space.m}>
                          <Text mx={theme.space.m}>申し込み後</Text>
                          <Select
                            disabled={props.disabled}
                            name="tel.delay"
                            error={formik.errors.tel?.delay}
                            value={formik.values.tel.delay}
                            onChange={(e) => {
                              const delay = e.currentTarget.value;
                              formik.setFieldValue('tel.delay', delay === '' ? '' : +delay);
                            }}
                          >
                            {Object.entries(TEL_DELAY).map(([delay, label]) => (
                              <option key={delay} value={delay}>
                                {label}
                              </option>
                            ))}
                          </Select>
                          <Text ml={theme.space.m}>に通知</Text>
                        </Flex>
                        {formik.errors.tel?.phoneNumber && (
                          <FieldError error={formik.errors.tel.phoneNumber} />
                        )}
                      </EntryList.Body>
                    </EntryList>
                  </TelSettingItem>
                )}
                <SettingItem
                  name="オンライン服薬指導"
                  content={
                    <>
                      オンライン服薬指導の希望時間
                      <br />
                      5分前にお知らせします
                    </>
                  }
                >
                  <Toggle
                    name="online"
                    checked={formik.values.online === 'enabled'}
                    onChange={(event) => handleChangeToggle(event, formik)}
                  />
                </SettingItem>
                {basicPlan && (
                  <SettingItem
                    name="服薬フォローアップ"
                    content={
                      <>
                        服薬フォローアップの回答が届いたら
                        <br />
                        通知音でお知らせします
                      </>
                    }
                  >
                    <Toggle
                      name="medicationFollowup"
                      checked={formik.values.medicationFollowup === 'enabled'}
                      onChange={(event) => handleChangeToggle(event, formik)}
                    />
                  </SettingItem>
                )}
              </List>
            </Box>
          </Box>
        );
      }}
    </Formik>
  );
});

SettingForm.displayName = 'SettingForm';
